import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import en from "./locales/en.json";
import fr from "./locales/fr.json";
import ar from "./locales/ar.json";

const STORAGE_KEY = "portfolio-locale";
const messages = { en, fr, ar };

const I18nContext = createContext(null);

function get(obj, path) {
  return path.split(".").reduce(
    (o, k) => (o && o[k] !== undefined ? o[k] : undefined),
    obj
  );
}

export function I18nProvider({ children }) {
  const [locale, setLocaleState] = useState(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved && messages[saved]) return saved;
    const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
    if (nav === "fr" || nav === "ar") return nav;
    return "en";
  });

  const isRtl = locale === "ar";

  useEffect(() => {
    document.documentElement.lang = locale;
    document.documentElement.dir = isRtl ? "rtl" : "ltr";
    localStorage.setItem(STORAGE_KEY, locale);
  }, [locale, isRtl]);

  const setLocale = (loc) => {
    if (messages[loc]) setLocaleState(loc);
  };

  const bundle = messages[locale] || en;

  const t = useCallback(
    (key, vars) => {
      let str = get(bundle, key);
      if (str === undefined) str = get(en, key);
      if (typeof str !== "string") return key;
      if (vars) {
        return str.replace(/\{\{(\w+)\}\}/g, (_, k) =>
          vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : ""
        );
      }
      return str;
    },
    [bundle]
  );

  const value = useMemo(
    () => ({ locale, setLocale, t, isRtl }),
    [locale, t, isRtl]
  );

  return (
    <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
  );
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
}
